/**
 * Leave-group applicability conflict rules:
 * - Org filters / All employees must not overlap another leave group's org/all scope.
 * - Individual employees are exclusive exceptions (they do not conflict with org/all scopes).
 * - The same employee cannot be individually selected on two leave groups.
 */

const ORG_KEYS = [
  "branches",
  "departments",
  "designations",
  "grades",
  "categories",
  "subDepartments",
];

const toIdList = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).filter(Boolean);
  }
  if (value == null || value === "") return [];
  return [String(value)];
};

const normalizeEmploymentTypes = (assignedTo = {}) => {
  const list = Array.isArray(assignedTo.employmentTypes)
    ? assignedTo.employmentTypes.map(String).filter(Boolean)
    : [];
  return list.length ? list : null;
};

export function normalizeLeaveGroupAssignedTo(assignedTo = {}) {
  return {
    allEmployees: !!assignedTo.allEmployees,
    branches: toIdList(assignedTo.branches ?? assignedTo.branch),
    departments: toIdList(assignedTo.departments ?? assignedTo.department),
    designations: toIdList(assignedTo.designations ?? assignedTo.designation),
    grades: toIdList(assignedTo.grades ?? assignedTo.grade),
    categories: toIdList(assignedTo.categories ?? assignedTo.category),
    subDepartments: toIdList(assignedTo.subDepartments ?? assignedTo.subDepartment),
    employmentTypes: Array.isArray(assignedTo.employmentTypes)
      ? assignedTo.employmentTypes.map(String).filter(Boolean)
      : [],
    employees: toIdList(assignedTo.employees).map(Number).filter((n) => n > 0),
  };
}

export function hasOrgOrAllScope(assignedTo = {}) {
  const a = normalizeLeaveGroupAssignedTo(assignedTo);
  if (a.allEmployees) return true;
  return ORG_KEYS.some((k) => a[k].length > 0) || a.employmentTypes.length > 0;
}

export function isIndividualOnlyScope(assignedTo = {}) {
  const a = normalizeLeaveGroupAssignedTo(assignedTo);
  return a.employees.length > 0 && !a.allEmployees && !hasOrgOrAllScope({ ...a, employees: [] });
}

function orgDimensionCompatible(aList, bList) {
  // Empty = "All" on that dimension → compatible with any specific value
  if (!aList.length || !bList.length) return true;
  const setB = new Set(bList.map(String));
  return aList.some((id) => setB.has(String(id)));
}

function employmentTypesOverlap(a, b) {
  const aEm = normalizeEmploymentTypes(a);
  const bEm = normalizeEmploymentTypes(b);
  if (!aEm || !bEm) return true; // empty = all employment types
  return aEm.some((t) => bEm.includes(t));
}

/** True when two org/all scopes can cover the same employees. */
export function orgScopesOverlap(assignedA = {}, assignedB = {}) {
  const a = normalizeLeaveGroupAssignedTo(assignedA);
  const b = normalizeLeaveGroupAssignedTo(assignedB);

  const aOrg = a.allEmployees || hasOrgOrAllScope({ ...a, employees: [] });
  const bOrg = b.allEmployees || hasOrgOrAllScope({ ...b, employees: [] });
  if (!aOrg || !bOrg) return false;

  if (a.allEmployees || b.allEmployees) {
    return employmentTypesOverlap(a, b);
  }

  for (const key of ORG_KEYS) {
    if (!orgDimensionCompatible(a[key], b[key])) return false;
  }
  return employmentTypesOverlap(a, b);
}

export function sharedIndividualEmployeeIds(assignedA = {}, assignedB = {}) {
  const a = normalizeLeaveGroupAssignedTo(assignedA);
  const b = normalizeLeaveGroupAssignedTo(assignedB);
  if (!a.employees.length || !b.employees.length) return [];
  const setB = new Set(b.employees.map(Number));
  return a.employees.filter((id) => setB.has(Number(id)));
}

/**
 * @returns {{ type: 'org'|'individual', otherGroup: object, sharedEmployeeIds?: number[], message: string }[]}
 */
export function findLeaveGroupApplicabilityConflicts({
  candidateAssignedTo,
  existingGroups = [],
  excludeGroupId = null,
  employeeLabelById = {},
} = {}) {
  const candidate = normalizeLeaveGroupAssignedTo(candidateAssignedTo);
  const conflicts = [];

  for (const group of existingGroups || []) {
    if (!group || group.isActive === false) continue;
    if (excludeGroupId != null && String(group.id) === String(excludeGroupId)) continue;

    const other = normalizeLeaveGroupAssignedTo(group.assignedTo || {});
    const sharedIds = sharedIndividualEmployeeIds(candidate, other);
    if (sharedIds.length) {
      const labels = sharedIds
        .slice(0, 5)
        .map((id) => employeeLabelById[id] || employeeLabelById[String(id)] || `Employee ${id}`);
      conflicts.push({
        type: "individual",
        otherGroup: group,
        sharedEmployeeIds: sharedIds,
        message: `Employee(s) already individually assigned to "${group.name}": ${labels.join(", ")}${
          sharedIds.length > 5 ? ` (+${sharedIds.length - 5} more)` : ""
        }. Remove them from the other group first, or clear them here.`,
      });
    }

    if (orgScopesOverlap(candidate, other)) {
      conflicts.push({
        type: "org",
        otherGroup: group,
        message: `Org filters / All employees overlap with leave group "${group.name}". Adjust filters so scopes do not cover the same people. Individually selected employees are exceptions and do not cause this conflict.`,
      });
    }
  }

  return conflicts;
}

export function summarizeLeaveGroupConflicts(conflicts = []) {
  if (!conflicts.length) return "";
  return conflicts.map((c) => c.message).join(" ");
}
